import { useCallback } from 'react';
import axios, { useApiRequest } from './axios';
import {
  ANALYTICS_ENDPOINT,
  buildEquipmentFullServiceStatsEndpoint,
  buildEquipmentGroupServiceStatsEndpoint,
  buildEquipmentServiceStatsEndpoint,
  buildInstanceHistoryEndpoint,
  buildTechnicianStatsEndpoint,
} from '../consts';
import {
  AnalyticsData,
  EquipmentFullServiceStats,
  EquipmentGroupServiceStats,
  EquipmentServiceStats,
  InstanceHistoryRecord,
  TechnicianServiceStats,
} from '../types/analytics';
import { CommonListResponse } from '../types/crud';
import { DateRangeValue } from '../components/ui/dateRangeFilter/dateRangeFilter.component';

export const useAnalytics = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const getAnalytics = useCallback(async (
    settlementsPeriod?: { year: number; month: number }
  ): Promise<AnalyticsData | undefined> => {
    const { data } = await executeRequest(() => axios.get(ANALYTICS_ENDPOINT, {
      params: {
        settlements_year: settlementsPeriod?.year,
        settlements_month: settlementsPeriod?.month,
      },
    }));
    return data;
  }, [executeRequest]);

  const getInstanceHistory = useCallback(async (
    modelName: string,
    objectId: string | number,
    page = 1,
    pageSize = 20,
  ): Promise<CommonListResponse<InstanceHistoryRecord> | undefined> => {
    const { data } = await executeRequest(() =>
      axios.get(buildInstanceHistoryEndpoint(modelName, objectId), {
        params: {
          page,
          page_size: pageSize,
        },
      })
    );

    return data;
  }, [executeRequest]);

  const getEquipmentServiceStats = useCallback(async (
    equipmentId: string | number
  ): Promise<EquipmentServiceStats | undefined> => {
    const { data } = await executeRequest(() =>
      axios.get(buildEquipmentServiceStatsEndpoint(equipmentId))
    );
    return data;
  }, [executeRequest]);

  const getEquipmentFullServiceStats = useCallback(async (
    equipmentId: string | number,
    dateRange?: DateRangeValue
  ): Promise<EquipmentFullServiceStats | undefined> => {
    const { data } = await executeRequest(() =>
      axios.get(buildEquipmentFullServiceStatsEndpoint(equipmentId), {
        params: {
          start_date: dateRange?.start_date || undefined,
          end_date: dateRange?.end_date || undefined,
        },
      })
    );
    return data;
  }, [executeRequest]);

  const getEquipmentGroupServiceStats = useCallback(async (
    groupId: string | number,
    dateRange?: DateRangeValue
  ): Promise<EquipmentGroupServiceStats | undefined> => {
    const { data } = await executeRequest(() =>
      axios.get(buildEquipmentGroupServiceStatsEndpoint(groupId), {
        params: {
          start_date: dateRange?.start_date || undefined,
          end_date: dateRange?.end_date || undefined,
        },
      })
    );
    return data;
  }, [executeRequest]);

  const getTechnicianStats = useCallback(async (
    technicianId: string | number,
    dateRange?: DateRangeValue
  ): Promise<TechnicianServiceStats | undefined> => {
    const { data } = await executeRequest(() =>
      axios.get(buildTechnicianStatsEndpoint(technicianId), {
        params: {
          start_date: dateRange?.start_date || undefined,
          end_date: dateRange?.end_date || undefined,
        },
      })
    );
    return data;
  }, [executeRequest]);

  return {
    loading,
    error,
    getAnalytics,
    getInstanceHistory,
    getEquipmentServiceStats,
    getEquipmentFullServiceStats,
    getEquipmentGroupServiceStats,
    getTechnicianStats,
  };
};
